'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By creating an account or using CareerLift ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service. These Terms form a legally binding agreement between you and CareerLift.`,
  },
  {
    id: 'eligibility',
    title: '2. Eligibility',
    content: `You must be at least 16 years old to use CareerLift. By using the Service, you represent that you meet this requirement and that all information you provide is accurate and complete.`,
  },
  {
    id: 'account',
    title: '3. Your Account',
    items: [
      { label: 'Registration', desc: 'You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.' },
      { label: 'Accuracy', desc: 'You agree to provide accurate, current, and complete information during registration and to keep it updated.' },
      { label: 'Security', desc: 'Notify us immediately at support@careerlift.app if you suspect unauthorised access to your account.' },
      { label: 'One Account', desc: 'Each person may maintain only one account. Creating multiple accounts to circumvent free-tier limits is prohibited.' },
    ],
  },
  {
    id: 'service',
    title: '4. The Service',
    content: `CareerLift provides AI-powered career tools including a resume builder, scholarship finder, career search, job analyzer, and video templates. The Service is provided "as is" and we reserve the right to modify, suspend, or discontinue any feature at any time with reasonable notice.`,
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use',
    content: `You agree not to:`,
    items: [
      { label: 'Misrepresentation', desc: 'Use AI-generated content to misrepresent your qualifications, fabricate employment history, or deceive employers.' },
      { label: 'Abuse', desc: 'Attempt to circumvent rate limits, reverse-engineer the Service, or exploit vulnerabilities.' },
      { label: 'Illegal Activity', desc: 'Use the Service for any unlawful purpose or in violation of any applicable law or regulation.' },
      { label: 'Harmful Content', desc: 'Submit content that is defamatory, harassing, discriminatory, or otherwise harmful.' },
      { label: 'Automated Scraping', desc: 'Use bots, scrapers, or automated tools to extract data from the Service without written permission.' },
    ],
  },
  {
    id: 'ai-content',
    title: '6. AI-Generated Content',
    content: `CareerLift uses OpenAI to generate resume suggestions and career guidance. AI-generated content is provided as a starting point only. You are solely responsible for reviewing, editing, and verifying all content before submitting it to employers or institutions. CareerLift makes no warranty that AI-generated content is accurate, complete, or appropriate for your specific situation.`,
  },
  {
    id: 'subscriptions',
    title: '7. Subscriptions & Billing',
    items: [
      { label: 'Free Tier', desc: 'Free accounts are subject to usage limits (5 resumes, 20 AI requests/month, 3 exports/month). Limits may change with notice.' },
      { label: 'Premium', desc: 'Premium subscriptions are billed monthly via Stripe. Prices are displayed on the Pricing page and may change with 30 days\' notice.' },
      { label: 'Cancellation', desc: 'You may cancel your subscription at any time. Access continues until the end of the current billing period.' },
      { label: 'Refunds', desc: 'We do not offer refunds for partial billing periods. If you experience a billing error, contact support@careerlift.app within 14 days.' },
      { label: 'Failed Payments', desc: 'If a payment fails, Premium access is suspended until payment is resolved. We will notify you via email.' },
    ],
  },
  {
    id: 'ip',
    title: '8. Intellectual Property',
    content: `CareerLift and its original content, features, and functionality are owned by CareerLift and protected by applicable intellectual property laws. You retain ownership of the resume content and personal data you submit. By using the Service, you grant CareerLift a limited licence to process your content solely to provide the Service.`,
  },
  {
    id: 'privacy',
    title: '9. Privacy',
    content: `Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review it to understand our data practices.`,
  },
  {
    id: 'disclaimers',
    title: '10. Disclaimers',
    content: `THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. CAREERLIFT DOES NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY SPECIFIC EMPLOYMENT OUTCOME WILL RESULT FROM USE OF THE SERVICE.`,
  },
  {
    id: 'liability',
    title: '11. Limitation of Liability',
    content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, CAREERLIFT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO CAREERLIFT IN THE 12 MONTHS PRECEDING THE CLAIM.`,
  },
  {
    id: 'termination',
    title: '12. Termination',
    content: `We may suspend or terminate your account at any time if you violate these Terms or engage in conduct we determine to be harmful to the Service or other users. You may delete your account at any time via the Data & Privacy settings page. Upon termination, your right to use the Service ceases immediately.`,
  },
  {
    id: 'governing-law',
    title: '13. Governing Law',
    content: `These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Delaware, and you consent to personal jurisdiction in those courts.`,
  },
  {
    id: 'changes',
    title: '14. Changes to Terms',
    content: `We may update these Terms from time to time. Material changes will be communicated via email or in-app notice at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.`,
  },
  {
    id: 'contact',
    title: '15. Contact',
    content: `For questions about these Terms, contact us at legal@careerlift.app.`,
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--void)' }}>
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="text-xs font-mono-code px-2.5 py-1 rounded-md"
              style={{ background: 'rgba(180,160,255,0.1)', color: 'var(--lavender)', border: '1px solid rgba(180,160,255,0.2)' }}
            >
              Legal
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--mono-white)' }}>
            Terms of Service
          </h1>
          <p className="text-sm" style={{ color: 'var(--mono-faint)' }}>
            Last updated: September 2026 · Effective immediately
          </p>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: 'var(--mono-dim)' }}>
            Please read these Terms carefully before using CareerLift. They govern your access to and use of all CareerLift products and services.
          </p>
        </div>

        {/* Quick nav */}
        <nav
          className="mb-12 p-5 rounded-xl"
          style={{ background: 'var(--glass)', border: '1px solid var(--glass-border)' }}
          aria-label="Terms sections"
        >
          <p className="text-xs font-mono-code uppercase tracking-widest mb-3" style={{ color: 'var(--mono-faint)' }}>
            Sections
          </p>
          <div className="flex flex-wrap gap-2">
            {SECTIONS?.map((s) => (
              <a
                key={s?.id}
                href={`#${s?.id}`}
                className="text-xs px-3 py-1.5 rounded-lg transition-colors"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--mono-dim)', border: '1px solid var(--glass-border)' }}
              >
                {s?.title?.replace(/^\d+\.\s/, '')}
              </a>
            ))}
          </div>
        </nav>

        {/* Sections */}
        <div className="space-y-10">
          {SECTIONS?.map((section) => (
            <section key={section?.id} id={section?.id} className="scroll-mt-24">
              <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--mono-white)' }}>
                {section?.title}
              </h2>
              {section?.content && (
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--mono-dim)' }}>
                  {section?.content}
                </p>
              )}
              {section?.items && (
                <ul className="space-y-3">
                  {section?.items?.map((item) => (
                    <li
                      key={item?.label}
                      className="flex gap-3 p-3.5 rounded-lg"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)' }}
                    >
                      <span className="text-xs font-semibold mt-0.5 shrink-0 w-32" style={{ color: 'var(--lavender)' }}>
                        {item?.label}
                      </span>
                      <span className="text-sm leading-relaxed" style={{ color: 'var(--mono-dim)' }}>
                        {item?.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* Footer CTA */}
        <div
          className="mt-16 p-6 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ background: 'rgba(180,160,255,0.05)', border: '1px solid rgba(180,160,255,0.15)' }}
        >
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--mono-white)' }}>Questions about these Terms?</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--mono-faint)' }}>Contact us at legal@careerlift.app — we respond within 5 business days.</p>
          </div>
          <Link
            href="/privacy-policy"
            className="shrink-0 text-sm font-semibold px-4 py-2.5 rounded-lg transition-all"
            style={{ background: 'rgba(180,160,255,0.1)', color: 'var(--lavender)', border: '1px solid rgba(180,160,255,0.2)' }}
          >
            Privacy Policy →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
